import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { apiFetch } from "../lib/api.js";
import { NODE_LABELS } from "../lib/operationPorts.js";
import type { OperationGraphNodeType } from "../lib/operationPorts.js";
import { cronToText, nextCronFire } from "../lib/cronDisplay.js";
import * as cronParserPkg from "cron-parser";

const { parseExpression } = (cronParserPkg as {
  parseExpression: typeof import("cron-parser").parseExpression;
} & {
  default?: { parseExpression: typeof import("cron-parser").parseExpression };
}).default ?? cronParserPkg;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TaskRunStatus = "pending" | "running" | "succeeded" | "failed" | "cancelled" | "skipped";
type WorkflowRunStatus = "pending" | "running" | "succeeded" | "failed" | "cancelled" | "timed_out" | "paused";

export type TaskRunItem = {
  id: string;
  task_id: string;
  status: TaskRunStatus;
  attempt: number;
  started_at: string | null;
  completed_at: string | null;
  output: Record<string, unknown> | null;
  summary: string | null;
  task_name: string;
  task_type: string;
  op_node_id: string | null;
  op_node_type: string | null;
  operation_id: string | null;
  operation_name: string | null;
  op_node_label: string | null;
};

type RunSummary = {
  id: string;
  workflow_id: string;
  workflow_name: string;
  status: WorkflowRunStatus;
  triggered_by: "schedule" | "manual";
  started_at: string | null;
  completed_at: string | null;
  summary: string | null;
  created_at: string;
  schedule_status: string | null;
  schedule_cron: string | null;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function statusPill(status: string, neverStarted = false): JSX.Element {
  if (neverStarted) return <span className="pill">not run</span>;
  const cls =
    status === "succeeded"
      ? "pill pillOk"
      : status === "failed" || status === "timed_out"
      ? "pill pillBad"
      : status === "running" || status === "pending" || status === "paused"
      ? "pill pillWarn"
      : "pill";
  return <span className={cls}>{status}</span>;
}

export function elapsed(start: string | null, end: string | null): string {
  if (!start) return "—";
  const s = new Date(start).getTime();
  const e = end ? new Date(end).getTime() : Date.now();
  const ms = e - s;
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  return `${Math.round(ms / 60000)}m`;
}

function formatTsParts(ts: string | null): { date: string; time: string } | null {
  if (!ts) return null;
  const d = new Date(ts);
  return {
    date: d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }),
    time: d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", second: "2-digit" }),
  };
}

function TsCell({ ts, width }: { ts: string | null; width: string }) {
  const parts = formatTsParts(ts);
  return (
    <span style={{ width, flexShrink: 0, display: "flex", flexDirection: "column", gap: 1 }}>
      {parts ? (
        <>
          <span className="mono muted" style={{ fontSize: 11, lineHeight: 1.2 }}>{parts.date}</span>
          <span className="mono muted" style={{ fontSize: 11, lineHeight: 1.2 }}>{parts.time}</span>
        </>
      ) : (
        <span className="mono muted" style={{ fontSize: 11 }}>—</span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Output modal
// ---------------------------------------------------------------------------

function fmtVal(val: unknown): string {
  if (val === null || val === undefined) return "—";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  if (typeof val === "number") return val.toLocaleString();
  if (typeof val === "string") return val;
  if (Array.isArray(val)) return `${val.length.toLocaleString()} rows`;
  return JSON.stringify(val);
}

function isRowsArray(val: unknown): val is Record<string, unknown>[] {
  return Array.isArray(val) && val.length > 0 && typeof val[0] === "object" && val[0] !== null;
}

function SectionLabel({ children }: { children: string }) {
  return (
    <div style={{
      fontSize: 10,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.07em",
      color: "var(--text-muted, #888)",
      padding: "8px 0 2px",
    }}>
      {children}
    </div>
  );
}

function KVTable({ rows }: { rows: { label: string; value: React.ReactNode }[] }) {
  return (
    <table className="table tableCompact" style={{ tableLayout: "fixed", width: "100%" }}>
      <colgroup><col style={{ width: "38%" }} /><col /></colgroup>
      <tbody>
        {rows.map((r) => (
          <tr key={r.label}>
            <td className="muted">{r.label}</td>
            <td>{r.value ?? <span className="muted">—</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Output fields consumed by the Execution section — excluded from "other" rendering
const EXECUTION_CONSUMED = new Set(["node_type", "node", "operation", "rows", "duration_ms", "rows_produced", "error", "_cancel_note"]);

function fmtDurationMs(ms: number): string {
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

function OutputTable({ output, taskRun }: { output: Record<string, unknown>; taskRun: TaskRunItem }) {
  const startedParts = formatTsParts(taskRun.started_at);
  const endedParts   = formatTsParts(taskRun.completed_at);

  // Task-level duration (wall clock)
  const taskDurationMs = taskRun.started_at && taskRun.completed_at
    ? new Date(taskRun.completed_at).getTime() - new Date(taskRun.started_at).getTime()
    : null;
  const taskDurationStr = taskDurationMs != null ? fmtDurationMs(taskDurationMs) : "—";

  // Execution metadata — pull from output fields written by the operation runner
  const rawNodeType    = typeof output.node_type === "string" ? output.node_type
                       : typeof output.node_type === "string" ? output.node_type
                       : taskRun.op_node_type ?? null;
  const nodeTypeLabel  = rawNodeType
    ? (NODE_LABELS[rawNodeType as OperationGraphNodeType] ?? rawNodeType)
    : null;

  // Name: prefer join data (authoritative), fall back to output fields
  const operationName = (
    [taskRun.operation_name, taskRun.op_node_label].filter(Boolean).join(" / ") ||
    [output.operation, output.node].filter((v) => typeof v === "string" && v).join(" / ")
  ) || null;

  const execRows       = typeof output.rows === "number"       ? output.rows
                       : typeof output.rows_produced === "number" ? output.rows_produced
                       : null;
  const execDurationMs = typeof output.duration_ms === "number" ? output.duration_ms : null;
  const execDurationStr = execDurationMs != null ? fmtDurationMs(execDurationMs) : taskDurationStr;

  const errorStr   = typeof output.error === "string"        ? output.error   : null;
  const cancelNote = typeof output._cancel_note === "string" ? output._cancel_note : null;

  const hasExecution = taskRun.task_type === "operation" && (nodeTypeLabel || operationName || execRows != null);

  // Remaining scalar fields not consumed above
  const otherEntries = Object.entries(output).filter(([k, v]) => !EXECUTION_CONSUMED.has(k) && !isRowsArray(v));
  const rowsArrays   = Object.entries(output).filter(([, v]) => isRowsArray(v)) as [string, Record<string, unknown>[]][];

  return (
    <div className="tableWrap" style={{ display: "grid", gap: 0 }}>
      <SectionLabel>Task</SectionLabel>
      <KVTable rows={[
        ...(taskRun.summary ? [{ label: "Summary", value: taskRun.summary }] : []),
        { label: "Started",   value: startedParts ? `${startedParts.date}  ${startedParts.time}` : "—" },
        { label: "Completed", value: endedParts   ? `${endedParts.date}  ${endedParts.time}`   : "—" },
        { label: "Duration",  value: taskDurationStr },
      ]} />

      {hasExecution ? (
        <>
          <SectionLabel>Execution</SectionLabel>
          <KVTable rows={[
            ...(nodeTypeLabel   ? [{ label: "Type",     value: nodeTypeLabel }] : []),
            ...(operationName   ? [{ label: "Name",     value: operationName }] : []),
            ...(execRows != null ? [{ label: "Rows",    value: execRows.toLocaleString() }] : []),
            { label: "Duration", value: execDurationStr },
          ]} />
        </>
      ) : null}

      {otherEntries.length > 0 ? (
        <>
          <SectionLabel>Output</SectionLabel>
          <KVTable rows={otherEntries.map(([k, v]) => ({ label: k.replace(/_/g, " "), value: fmtVal(v) }))} />
        </>
      ) : null}

      {(errorStr || cancelNote) ? (
        <>
          <SectionLabel>Notes</SectionLabel>
          <KVTable rows={[
            ...(errorStr   ? [{ label: "Error",  value: <span style={{ color: "var(--danger-text, #b91c1c)", wordBreak: "break-word" as const }}>{errorStr}</span> }] : []),
            ...(cancelNote ? [{ label: "Notice", value: <span className="muted" style={{ wordBreak: "break-word" as const }}>{cancelNote}</span> }] : []),
          ]} />
        </>
      ) : null}

      {rowsArrays.map(([key, rowData]) => {
        const cols = Object.keys(rowData[0] ?? {});
        return (
          <div key={key} style={{ marginTop: 12 }}>
            <SectionLabel>{key.replace(/_/g, " ")}</SectionLabel>
            <table className="table tableCompact">
              <thead><tr>{cols.map((c) => <th key={c}>{c.replace(/_/g, " ")}</th>)}</tr></thead>
              <tbody>
                {rowData.map((row, i) => (
                  <tr key={i}>{cols.map((c) => <td key={c}>{fmtVal(row[c])}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

export function OutputModal({ taskRun, onClose }: { taskRun: TaskRunItem; onClose: () => void }) {
  return (
    <div className="modalOverlay" role="presentation" onClick={onClose}>
      <div
        className="modal modalScrollable"
        role="dialog"
        aria-modal="true"
        style={{ maxWidth: 620 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modalScrollHeader">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div className="reportModalTitle" style={{ flex: 1 }}>{taskRun.task_name}</div>
            {statusPill(taskRun.status)}
          </div>
          {taskRun.attempt > 1 ? (
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Attempt {taskRun.attempt}</div>
          ) : null}
        </div>
        <div className="modalScrollBody">
          {taskRun.output && Object.keys(taskRun.output).length > 0 ? (
            <OutputTable output={taskRun.output} taskRun={taskRun} />
          ) : (
            <OutputTable output={{}} taskRun={taskRun} />
          )}
        </div>
        <div className="modalScrollFooter">
          <div className="actions">
            <button className="btn" type="button" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Activity panel
// ---------------------------------------------------------------------------

function ActivityPanel() {
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [taskRunsCache, setTaskRunsCache] = useState<Map<string, TaskRunItem[]>>(new Map());
  const [loadingTasks, setLoadingTasks] = useState<Set<string>>(new Set());
  const [outputModal, setOutputModal] = useState<TaskRunItem | null>(null);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [cancelling, setCancelling] = useState<Set<string>>(new Set());
  const [cancellingTask, setCancellingTask] = useState<Set<string>>(new Set());
  const [pausingRun, setPausingRun] = useState<Set<string>>(new Set());
  const [resumingRun, setResumingRun] = useState<Set<string>>(new Set());

  const runsRef = useRef<RunSummary[]>([]);
  const expandedRef = useRef<Set<string>>(new Set());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => { runsRef.current = runs; }, [runs]);
  useEffect(() => { expandedRef.current = expanded; }, [expanded]);

  const loadTaskRuns = useCallback(async (runId: string) => {
    setLoadingTasks((prev) => new Set(prev).add(runId));
    try {
      const data = await apiFetch<{ workflow_run: { task_runs: TaskRunItem[] } }>(
        `/api/tenant/workflow-runs/${encodeURIComponent(runId)}`
      );
      setTaskRunsCache((prev) => new Map(prev).set(runId, data.workflow_run.task_runs));
    } catch {
      // non-fatal
    } finally {
      setLoadingTasks((prev) => { const s = new Set(prev); s.delete(runId); return s; });
    }
  }, []);

  const fetchPage = useCallback(async (cursor?: string) => {
    const params = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
    return apiFetch<{ runs: RunSummary[]; next_cursor: string | null }>(
      `/api/tenant/activity/runs${params}`
    );
  }, []);

  const load = useCallback(async (showLoading: boolean) => {
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const data = await fetchPage();
      setRuns(data.runs);
      setNextCursor(data.next_cursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [fetchPage]);

  // Refresh: merge updated runs into existing list, then refresh task runs for
  // expanded running runs.
  const refresh = useCallback(async () => {
    try {
      const data = await fetchPage();
      setRuns((prev) => {
        const updated = new Map(data.runs.map((r) => [r.id, r]));
        // Replace known runs with fresh data; keep any extra loaded-more runs at bottom
        const merged = prev.map((r) => updated.get(r.id) ?? r);
        // Prepend any brand-new runs not yet in the list
        const existingIds = new Set(prev.map((r) => r.id));
        const newRuns = data.runs.filter((r) => !existingIds.has(r.id));
        return [...newRuns, ...merged];
      });
      setNextCursor(data.next_cursor);

      // Refresh task runs for expanded running runs
      const currentExpanded = expandedRef.current;
      const currentRuns = runsRef.current;
      for (const runId of currentExpanded) {
        const run = currentRuns.find((r) => r.id === runId);
        if (run && run.status === "running") {
          void loadTaskRuns(runId);
        }
      }
    } catch {
      // silent — don't disrupt visible state on background refresh failure
    }
  }, [fetchPage, loadTaskRuns]);

  useEffect(() => {
    void load(true);
    intervalRef.current = setInterval(() => { void refresh(); }, 5000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [load, refresh]);

  async function loadMore() {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const data = await fetchPage(nextCursor);
      setRuns((prev) => [...prev, ...data.runs]);
      setNextCursor(data.next_cursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingMore(false);
    }
  }

  function toggleExpand(runId: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) {
        next.delete(runId);
      } else {
        next.add(runId);
        if (!taskRunsCache.has(runId)) {
          void loadTaskRuns(runId);
        }
      }
      return next;
    });
  }

  async function cancelRun(runId: string) {
    setCancelling((prev) => new Set(prev).add(runId));
    try {
      await apiFetch(`/api/tenant/workflow-runs/${encodeURIComponent(runId)}/cancel`, { method: "POST" });
      await load(false);
    } catch {
      // best effort
    } finally {
      setCancelling((prev) => { const s = new Set(prev); s.delete(runId); return s; });
    }
  }

  async function cancelTaskRunFn(runId: string, taskRunId: string) {
    setCancellingTask((prev) => new Set(prev).add(taskRunId));
    try {
      await apiFetch(
        `/api/tenant/workflow-runs/${encodeURIComponent(runId)}/task-runs/${encodeURIComponent(taskRunId)}/cancel`,
        { method: "POST" }
      );
      // Refresh task runs for this workflow run
      void loadTaskRuns(runId);
    } catch {
      // best effort
    } finally {
      setCancellingTask((prev) => { const s = new Set(prev); s.delete(taskRunId); return s; });
    }
  }

  async function pauseRun(run: RunSummary) {
    setPausingRun((prev) => new Set(prev).add(run.id));
    try {
      await apiFetch(`/api/tenant/workflow-runs/${encodeURIComponent(run.id)}/pause`, { method: "POST" });
      setRuns((prev) => prev.map((r) => r.id === run.id ? { ...r, status: "paused" } : r));
    } catch {
      // best effort
    } finally {
      setPausingRun((prev) => { const s = new Set(prev); s.delete(run.id); return s; });
    }
  }

  async function resumeRun(run: RunSummary) {
    setResumingRun((prev) => new Set(prev).add(run.id));
    try {
      await apiFetch(`/api/tenant/workflow-runs/${encodeURIComponent(run.id)}/resume`, { method: "POST" });
      setRuns((prev) => prev.map((r) => r.id === run.id ? { ...r, status: "running" } : r));
    } catch {
      // best effort
    } finally {
      setResumingRun((prev) => { const s = new Set(prev); s.delete(run.id); return s; });
    }
  }

  if (loading) return <LoadingState />;
  if (error) return <ErrorState detail={error} />;
  if (runs.length === 0) return <div className="adminListEmpty">No workflow runs yet.</div>;

  const COL = { arrow: "16px", ts: "128px", dur: "44px" };

  const q = search.trim().toLowerCase();

  // Resolve date range preset → [fromMs, toMs]
  function resolveRange(): [number | null, number | null] {
    const now = new Date();
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const endOfDay   = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).getTime();
    switch (dateRange) {
      case "today":    return [startOfDay(now), endOfDay(now)];
      case "yesterday": { const y = new Date(now); y.setDate(y.getDate() - 1); return [startOfDay(y), endOfDay(y)]; }
      case "7d":  return [now.getTime() - 7  * 86400000, null];
      case "30d": return [now.getTime() - 30 * 86400000, null];
      case "custom": return [
        customFrom ? new Date(customFrom).getTime() : null,
        customTo   ? new Date(customTo + "T23:59:59").getTime() : null,
      ];
      default: return [null, null];
    }
  }
  const [fromMs, toMs] = resolveRange();

  const visibleRuns = runs.filter((r) => {
    if (q && !r.workflow_name.toLowerCase().includes(q)) return false;
    if (fromMs != null) {
      const ts = r.started_at ? new Date(r.started_at).getTime() : null;
      if (ts == null || ts < fromMs) return false;
    }
    if (toMs != null) {
      const ts = r.started_at ? new Date(r.started_at).getTime() : null;
      if (ts == null || ts > toMs) return false;
    }
    return true;
  });

  return (
    <div style={{ display: "grid", gap: 4 }}>
      {/* Search + date filter */}
      <div className="toolbar">
        <div className="toolbarGroup" style={{ flexWrap: "nowrap" }}>
          <input
            className="input"
            type="search"
            placeholder="Search activity…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 180, fontSize: 13, flexShrink: 0 }}
          />
          <select
            className="select"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            style={{ fontSize: 13, flexShrink: 0 }}
          >
            <option value="all">All time</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="custom">Custom range…</option>
          </select>
        </div>
      </div>

      {dateRange === "custom" ? (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 10px",
          background: "var(--surface2, #f4f4f4)",
          borderRadius: 6,
          fontSize: 13,
        }}>
          <span className="muted" style={{ fontSize: 12, whiteSpace: "nowrap" }}>From</span>
          <input
            className="input"
            type="date"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            style={{ fontSize: 12, flex: 1 }}
          />
          <span className="muted" style={{ fontSize: 12 }}>to</span>
          <input
            className="input"
            type="date"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            style={{ fontSize: 12, flex: 1 }}
          />
        </div>
      ) : null}

      {/* Column headers */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "2px 10px", fontSize: 11 }}>
        <span style={{ width: COL.arrow, flexShrink: 0 }} />
        <span className="muted" style={{ width: COL.ts, flexShrink: 0 }}>Start</span>
        <span className="muted" style={{ width: COL.ts, flexShrink: 0 }}>End</span>
        <span className="muted" style={{ width: COL.dur, flexShrink: 0, textAlign: "right" }}>Time</span>
        <span className="muted" style={{ flex: 1 }}>Workflow</span>
        <span className="muted">Status</span>
      </div>
      {visibleRuns.length === 0 ? (
        <div className="adminListEmpty">No runs match the current filters.</div>
      ) : null}
      {visibleRuns.map((run) => {
        const isExpanded = expanded.has(run.id);
        const tasks = taskRunsCache.get(run.id) ?? [];
        const tasksLoading = loadingTasks.has(run.id);
        const canPause = run.status === "running";
        const canResume = run.status === "paused";
        const canCancel = run.status === "running" || run.status === "paused";

        // COL widths defined at panel level above

        return (
          <div key={run.id} className="card" style={{ padding: 0, overflow: "hidden" }}>
            {/* Workflow run row */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 10px",
                cursor: "pointer",
                fontSize: 12,
              }}
              onClick={() => toggleExpand(run.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") toggleExpand(run.id); }}
            >
              {/* Arrow */}
              <span className="muted" style={{ width: COL.arrow, flexShrink: 0, fontSize: 10 }}>
                {isExpanded ? "▼" : "▶"}
              </span>

              {/* Start */}
              <TsCell ts={run.started_at} width={COL.ts} />

              {/* End */}
              <TsCell ts={run.completed_at} width={COL.ts} />

              {/* Duration */}
              <span className="muted" style={{ width: COL.dur, flexShrink: 0, textAlign: "right" }}>
                {elapsed(run.started_at, run.completed_at)}
              </span>

              {/* Workflow name → canvas */}
              <Link
                to={`/app/schedule/workflows/${run.workflow_id}`}
                style={{ flex: 1, fontWeight: 600, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                onClick={(e) => e.stopPropagation()}
              >
                {run.workflow_name}
              </Link>

              {/* Status */}
              {statusPill(run.status)}

              {/* Actions */}
              {(canPause || canResume || canCancel) ? (
                <div style={{ display: "flex", gap: 4 }} onClick={(e) => e.stopPropagation()}>
                  {canPause ? (
                    <button
                      className="btn"
                      type="button"
                      style={{ fontSize: 11, padding: "1px 6px" }}
                      disabled={pausingRun.has(run.id)}
                      onClick={() => void pauseRun(run)}
                    >
                      {pausingRun.has(run.id) ? "Pausing…" : "Pause"}
                    </button>
                  ) : null}
                  {canResume ? (
                    <button
                      className="btn btnPrimary"
                      type="button"
                      style={{ fontSize: 11, padding: "1px 6px" }}
                      disabled={resumingRun.has(run.id)}
                      onClick={() => void resumeRun(run)}
                    >
                      {resumingRun.has(run.id) ? "Resuming…" : "Resume"}
                    </button>
                  ) : null}
                  {canCancel ? (
                    <button
                      className="btn btnDanger"
                      type="button"
                      style={{ fontSize: 11, padding: "1px 6px" }}
                      disabled={cancelling.has(run.id)}
                      onClick={() => void cancelRun(run.id)}
                    >
                      {cancelling.has(run.id) ? "Cancelling…" : "Cancel"}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>

            {/* Expanded task runs */}
            {isExpanded ? (
              <div style={{ borderTop: "1px solid var(--border)" }}>
                {tasksLoading ? (
                  <div className="muted" style={{ padding: "5px 10px 5px 34px", fontSize: 12 }}>Loading…</div>
                ) : tasks.length === 0 ? (
                  <div className="muted" style={{ padding: "5px 10px 5px 34px", fontSize: 12 }}>No tasks.</div>
                ) : (
                  tasks.map((tr) => (
                    <div
                      key={tr.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "5px 10px",
                        paddingLeft: 34,  // indent = arrow width + gap
                        borderBottom: "1px solid var(--border)",
                        fontSize: 12,
                      }}
                    >
                      {/* Start */}
                      <TsCell ts={tr.started_at} width={COL.ts} />

                      {/* End */}
                      <TsCell ts={tr.completed_at} width={COL.ts} />

                      {/* Duration */}
                      <span className="muted" style={{ width: COL.dur, flexShrink: 0, textAlign: "right" }}>
                        {elapsed(tr.started_at, tr.completed_at)}
                      </span>

                      {/* Task name → canvas with task selected */}
                      <Link
                        to={`/app/schedule/workflows/${run.workflow_id}?task=${tr.task_id}`}
                        style={{ minWidth: 80, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {tr.task_name}
                      </Link>

                      {/* Type: operation name / node name */}
                      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {tr.operation_id && tr.operation_name ? (
                          <>
                            <span className="muted">{tr.task_type}: </span>
                            <Link
                              to={`/app/build/operations/${tr.operation_id}${tr.op_node_id ? `?node=${tr.op_node_id}` : ""}`}
                              style={{ fontSize: 12 }}
                            >
                              {tr.operation_name}
                              {tr.op_node_label ? ` / ${tr.op_node_label}` : ""}
                            </Link>
                          </>
                        ) : (
                          <span className="muted">{tr.task_type}</span>
                        )}
                      </span>

                      {/* Output link */}
                      <button
                        className="adminListAction"
                        type="button"
                        style={{ fontSize: 11, flexShrink: 0 }}
                        onClick={() => setOutputModal(tr)}
                      >
                        Output
                      </button>

                      {/* Cancel task — only for running tasks */}
                      {tr.status === "running" ? (
                        <button
                          className="adminListAction isDanger"
                          type="button"
                          style={{ fontSize: 11, flexShrink: 0 }}
                          disabled={cancellingTask.has(tr.id)}
                          onClick={() => void cancelTaskRunFn(run.id, tr.id)}
                        >
                          {cancellingTask.has(tr.id) ? "…" : "Cancel"}
                        </button>
                      ) : null}

                      {/* Status — distinguish never-started cancelled from mid-run cancelled */}
                      {statusPill(tr.status, tr.status === "cancelled" && tr.started_at === null)}
                    </div>
                  ))
                )}
              </div>
            ) : null}
          </div>
        );
      })}

      {nextCursor ? (
        <div style={{ textAlign: "center", paddingTop: 8 }}>
          <button
            className="btn"
            type="button"
            onClick={() => void loadMore()}
            disabled={loadingMore}
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      ) : null}

      {outputModal ? (
        <OutputModal taskRun={outputModal} onClose={() => setOutputModal(null)} />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upcoming panel — helpers
// ---------------------------------------------------------------------------

type UpcomingSchedule = {
  id: string;
  name: string;
  schedule_cron: string | null;
  schedule_status: string;
  schedule_effective_date: string | null;
  schedule_end_date: string | null;
};


function cadenceSummaryText(cron: string | null, _config?: Record<string, unknown>): string {
  return cronToText(cron);
}

/** Compute the next UTC Date a schedule fires after now. Returns null if indeterminate. */
function computeNextRun(
  cron: string | null,
  _config: Record<string, unknown> | null,
  effectiveDate: string | null,
  endDate: string | null,
  timezone: string
): Date | null {
  return nextCronFire(cron, timezone, { effectiveDate, endDate });
}

function formatNextRunDate(date: Date, timezone: string): string {
  return date.toLocaleString("en-US", {
    timeZone: timezone,
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/** Returns the set of day-of-month numbers (1-based) on which this schedule fires. */
function getScheduledDaysInMonth(
  cron: string | null,
  _config: Record<string, unknown> | null,
  effectiveDate: string | null,
  endDate: string | null,
  year: number,
  month: number,
  timezone: string
): Set<number> {
  const days = new Set<number>();
  if (!cron) return days;
  const startUtc = new Date(Date.UTC(year, month - 1, 1, 0, 0, 0));
  const endUtc = new Date(Date.UTC(year, month, 1, 0, 0, 0));
  const effDate = effectiveDate ? new Date(effectiveDate) : null;
  const endLimit = endDate ? new Date(endDate) : null;
  const probeStart = effDate && effDate > startUtc ? effDate : startUtc;
  if (endLimit && endLimit < probeStart) return days;
  try {
    const expr = parseExpression(cron, { tz: timezone, currentDate: probeStart, endDate: endUtc, iterator: false });
    while (true) {
      try {
        const next = expr.next().toDate();
        if (next >= endUtc) break;
        if (endLimit && next > endLimit) break;
        const localDay = new Intl.DateTimeFormat("en-US", { timeZone: timezone, day: "numeric" }).format(next);
        days.add(parseInt(localDay, 10));
      } catch {
        break;
      }
    }
  } catch {
    /* invalid cron — return empty set */
  }
  return days;
}

// ---------------------------------------------------------------------------
// Upcoming panel — list view
// ---------------------------------------------------------------------------

function ListView({ schedules, timezone }: { schedules: UpcomingSchedule[]; timezone: string }) {
  const withNext = schedules
    .map((s) => ({
      ...s,
      nextRun: computeNextRun(s.schedule_cron, null, s.schedule_effective_date, s.schedule_end_date, timezone),
    }))
    .sort((a, b) => {
      if (!a.nextRun) return 1;
      if (!b.nextRun) return -1;
      return a.nextRun.getTime() - b.nextRun.getTime();
    });

  if (withNext.length === 0) return <div className="adminListEmpty">No active schedules.</div>;

  return (
    <div className="tableWrap">
      <table className="table">
        <thead>
          <tr>
            <th>Workflow</th>
            <th>Next run</th>
            <th>Schedule</th>
          </tr>
        </thead>
        <tbody>
          {withNext.map((s) => (
            <tr key={s.id}>
              <td>
                <Link to={`/app/schedule/workflows/${s.id}`}>{s.name}</Link>
              </td>
              <td className="mono" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                {s.nextRun ? formatNextRunDate(s.nextRun, timezone) : "—"}
              </td>
              <td className="muted" style={{ fontSize: 12 }}>
                {cadenceSummaryText(s.schedule_cron)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upcoming panel — calendar view
// ---------------------------------------------------------------------------

const FULL_MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Stable colors cycling per workflow index
const DOT_COLORS = [
  "#3b82f6", "#10b981", "#f59e0b", "#ef4444",
  "#8b5cf6", "#06b6d4", "#ec4899", "#84cc16",
];

function CalendarView({ schedules, timezone }: { schedules: UpcomingSchedule[]; timezone: string }) {
  const todayUtc = new Date();
  const [year, setYear] = useState(todayUtc.getFullYear());
  const [month, setMonth] = useState(todayUtc.getMonth() + 1);

  // Get today's date in tenant timezone for highlighting
  const todayTzStr = todayUtc.toLocaleDateString("en-CA", { timeZone: timezone }); // YYYY-MM-DD
  const [todayYear, todayMonthVal, todayDay] = todayTzStr.split("-").map(Number) as [number, number, number];

  function prevMonth() {
    if (month === 1) { setYear((y) => y - 1); setMonth(12); }
    else setMonth((m) => m - 1);
  }
  function nextMonth() {
    if (month === 12) { setYear((y) => y + 1); setMonth(1); }
    else setMonth((m) => m + 1);
  }

  // Map: day → list of {name, color}
  const dayEntries = new Map<number, { name: string; color: string }[]>();
  schedules.forEach((s, idx) => {
    const color = DOT_COLORS[idx % DOT_COLORS.length] ?? "#3b82f6";
    const fired = getScheduledDaysInMonth(
      s.schedule_cron, null,
      s.schedule_effective_date, s.schedule_end_date,
      year, month, timezone
    );
    for (const d of fired) {
      if (!dayEntries.has(d)) dayEntries.set(d, []);
      dayEntries.get(d)!.push({ name: s.name, color });
    }
  });

  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const firstWeekday = new Date(Date.UTC(year, month - 1, 1)).getUTCDay(); // 0=Sun

  // Build grid cells
  const cells: (number | null)[] = Array(firstWeekday).fill(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <button className="btn" type="button" onClick={prevMonth} style={{ padding: "2px 10px" }}>‹</button>
        <span style={{ minWidth: 140, textAlign: "center", fontWeight: 600 }}>
          {FULL_MONTHS[month - 1]} {year}
        </span>
        <button className="btn" type="button" onClick={nextMonth} style={{ padding: "2px 10px" }}>›</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
          <div key={d} className="muted" style={{ textAlign: "center", fontSize: 11, padding: "2px 0" }}>{d}</div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
        {cells.map((day, i) => {
          if (!day) return <div key={`blank-${i}`} />;
          const isToday = year === todayYear && month === todayMonthVal && day === todayDay;
          const entries = dayEntries.get(day) ?? [];
          return (
            <div
              key={day}
              style={{
                borderRadius: 4,
                padding: "4px 3px",
                minHeight: 46,
                background: isToday ? "var(--accent-subtle, #eff6ff)" : undefined,
                border: `1px solid ${isToday ? "var(--accent, #3b82f6)" : "transparent"}`,
              }}
            >
              <div style={{ textAlign: "center", fontSize: 12, fontWeight: isToday ? 700 : 400 }}>{day}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "center", marginTop: 3 }}>
                {entries.slice(0, 4).map((e, j) => (
                  <div
                    key={j}
                    title={e.name}
                    style={{ width: 6, height: 6, borderRadius: "50%", background: e.color, flexShrink: 0 }}
                  />
                ))}
                {entries.length > 4 ? (
                  <span className="muted" style={{ fontSize: 9, lineHeight: "6px" }}>+{entries.length - 4}</span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {schedules.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", marginTop: 12 }}>
          {schedules.map((s, idx) => (
            <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: DOT_COLORS[idx % DOT_COLORS.length], flexShrink: 0 }} />
              <Link to={`/app/schedule/workflows/${s.id}`}>{s.name}</Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upcoming panel
// ---------------------------------------------------------------------------

function UpcomingPanel({ timezone }: { timezone: string }) {
  const [schedules, setSchedules] = useState<UpcomingSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [calView, setCalView] = useState(false);

  useEffect(() => {
    apiFetch<{ schedules: UpcomingSchedule[] }>("/api/tenant/activity/upcoming")
      .then((d) => setSchedules(d.schedules))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState detail={error} />;

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div className="toolbar">
        <div className="toolbarGroup">
          <button className={`btn${!calView ? " btnPrimary" : ""}`} type="button" onClick={() => setCalView(false)}>List</button>
          <button className={`btn${calView ? " btnPrimary" : ""}`} type="button" onClick={() => setCalView(true)}>Calendar</button>
        </div>
      </div>
      {calView ? (
        <div className="card">
          <CalendarView schedules={schedules} timezone={timezone} />
        </div>
      ) : (
        <ListView schedules={schedules} timezone={timezone} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export function ScheduleActivity() {
  const [view, setView] = useState<"activity" | "upcoming">("activity");
  const [timezone, setTimezone] = useState("UTC");

  useEffect(() => {
    apiFetch<{ timezone: string }>("/api/tenant/settings")
      .then((d) => setTimezone(d.timezone ?? "UTC"))
      .catch(() => {});
  }, []);

  return (
    <PageLayout title="Activity">
      <div style={{ display: "grid", gap: 12 }}>
        <div className="toolbar">
          <div className="toolbarGroup">
            <button
              className={`btn${view === "activity" ? " btnPrimary" : ""}`}
              type="button"
              onClick={() => setView("activity")}
            >
              Activity
            </button>
            <button
              className={`btn${view === "upcoming" ? " btnPrimary" : ""}`}
              type="button"
              onClick={() => setView("upcoming")}
            >
              Upcoming
            </button>
          </div>
        </div>

        {view === "activity" ? <ActivityPanel /> : <UpcomingPanel timezone={timezone} />}
      </div>
    </PageLayout>
  );
}
